import React, { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Paperclip, X, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Small self-contained file-attachment picker.
 * Reads a chosen file as a base64 dataURL and calls `onChange(dataURL, fileName)`.
 * Renders an inline preview (thumb for images, filename chip for PDFs).
 *
 * Props:
 *   value       : dataURL string (or "")
 *   onChange    : (dataURL, fileName) => void
 *   label       : label shown above the field
 *   accept      : file input `accept` attr (defaults to image/* + PDF)
 *   maxSizeMB   : reject files larger than this
 *   testid      : test-id prefix
 */
export default function FileAttachment({
  value = "",
  onChange,
  label = "Attach File",
  accept = "image/*,application/pdf",
  maxSizeMB = 2,
  testid = "attachment",
}) {
  const ref = useRef(null);

  const pick = () => ref.current?.click();

  const handle = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File exceeds ${maxSizeMB} MB limit`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result), f.name);
    reader.onerror = () => toast.error("Could not read file");
    reader.readAsDataURL(f);
    e.target.value = "";  // allow re-selecting same file later
  };

  const isImage = value?.startsWith?.("data:image/");
  const isPdf = value?.startsWith?.("data:application/pdf");

  return (
    <div className="space-y-1.5">
      <Label className="eyebrow">{label}</Label>
      {!value && (
        <div>
          <input
            ref={ref} type="file" accept={accept}
            className="hidden" onChange={handle}
            data-testid={`${testid}-input`}
          />
          <Button type="button" variant="outline" size="sm" onClick={pick}
            className="h-9" data-testid={`${testid}-pick-btn`}>
            <Paperclip size={14} className="mr-1.5" /> Choose file
          </Button>
          <span className="ml-2 text-[11px] text-muted-foreground">
            Max {maxSizeMB} MB · Images or PDF
          </span>
        </div>
      )}
      {value && (
        <div className="flex items-start gap-3 p-2 border rounded-md bg-muted/20">
          {isImage ? (
            <img src={value} alt="attachment"
              style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 4 }} />
          ) : (
            <div className="w-[72px] h-[72px] rounded-md bg-muted flex items-center justify-center">
              <FileText size={28} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[12px] font-mono truncate">
              {isImage ? "Image attached" : isPdf ? "PDF attached" : "File attached"}
            </div>
            <div className="text-[11px] text-muted-foreground">
              Approx {Math.round(value.length / 1024)} KB
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <a href={value} target="_blank" rel="noreferrer"
                className="text-[11px] underline underline-offset-2 hover:text-primary"
                data-testid={`${testid}-view-link`}>
                View
              </a>
              <button type="button"
                onClick={() => onChange("", "")}
                className="text-[11px] text-destructive inline-flex items-center gap-0.5"
                data-testid={`${testid}-remove-btn`}>
                <X size={12} /> Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
